/**
 * ecosystem-web.js
 *
 * Renders the "Tools & Stakeholders" ecosystem web (D3). Two variants:
 *   #ecosystem-web        — the full web: tools → operators → governance → outputs
 *   #ecosystem-web-right  — the same, but the operators tier collapses into a
 *                           single Stance hub: tools → STANCE → governance → outputs
 *
 * Fixed-layout nodes (hex / person-circle / square / logo) connected by bowed
 * dashed edges, revealed phase-by-phase on load (dashed lines revealed via a
 * mask so the dashes stay dashes). Respects prefers-reduced-motion.
 *
 * Uses only d3-selection + d3-transition (adds .transition to selections) +
 * d3-ease, not full d3.
 */

import { select } from 'd3-selection';
import 'd3-transition';
import { easeCubicOut, easeBackOut } from 'd3-ease';

// Node icons — inlined as raw SVG so stroke="currentColor" recolors to --icon.
// (The Stance logo carries its own gradient fills and ignores color.)
import siemSvg from '../assets/images/siem.svg?raw';
import privacySvg from '../assets/images/privacy.svg?raw';
import cloudSvg from '../assets/images/cloud.svg?raw';
import itsmSvg from '../assets/images/itsm.svg?raw';
import userSvg from '../assets/images/user.svg?raw';
import grcSvg from '../assets/images/grc.svg?raw';
import registerSvg from '../assets/images/internal-control-register.svg?raw';
import extAuditSvg from '../assets/images/external-auditor.svg?raw';
import stanceSvg from '../assets/images/stance-logo-mark.svg?raw';

const ICONS = {
  siem: siemSvg,
  privacy: privacySvg,
  cloud: cloudSvg,
  itsm: itsmSvg,
  user: userSvg,
  grc: grcSvg,
  register: registerSvg,
  extaudit: extAuditSvg,
  stance: stanceSvg,
};

// ── Geometry / layout config ────────────────────────────────────────────────
const HEX_R = 145;
const CIRCLE_R = 113;
const SQUARE_R = 118; // half the square side
const LOGO_R = 170; // Stance hub radius

// Vertical center (y) of each tier's nodes. Change to adjust the gap between
// tiers — the group-frame + viewBox height derive from `outputs`.
const TIER_Y = { tools: 205, operators: 690, governance: 1100, outputs: 1625 };

// Right variant stacks four rows — tools, Stance hub, caption, outputs. The hub
// and caption sit near the 1/3 and 2/3 marks of the tools→outputs span; nudge
// the hub down a touch with STANCE_DROP.
const RIGHT_ROW_GAP = (TIER_Y.outputs - TIER_Y.tools) / 3;
const STANCE_DROP = 130; // px the Stance hub sits below the even 1/3 mark
const STANCE_Y = TIER_Y.tools + RIGHT_ROW_GAP + STANCE_DROP;
// Caption is grouped directly under the hub (no connector). Its center sits a
// fixed offset below the hub center — smaller CAPTION_GAP = tighter to the logo.
const CAPTION_GAP = 292;
const CAPTION_Y = STANCE_Y + CAPTION_GAP;

// Node → label gap per tier (by node.phase). Lower = label closer to the node.
const LABEL_GAP = { 0: 54, 1: 54, 2: 54, 3: 54 };
// Vertical spacing between wrapped label lines (SVG <tspan> dy — NOT line-height).
const LABEL_LINE = 44;

// Right-variant caption block — baseline y of each row, measured from the
// caption node's center. Adjust these to change the SPACING between the three
// rows. (Font size / weight / color / tracking live in the `.eco .tl-*` rules
// in sections/what-we-do.html.)
const CAPTION_ROWS = { eyebrow: -68, heading: 24, sub: 96 };

// Half-height of the "independent test layer" caption block (right variant),
// used as its funnel anchor so the connector meets its top/bottom edges.
const TEXT_R = 96;

// Corner radius for the right-variant elbow (orthogonal) connectors.
const ELBOW_R = 70;

const iconSize = (shape) =>
  shape === 'hex' ? 92 : shape === 'square' ? 84 : shape === 'logo' ? 280 : 76;
const nodeRadius = (shape) =>
  shape === 'hex'
    ? HEX_R
    : shape === 'square'
      ? SQUARE_R
      : shape === 'logo'
        ? LOGO_R
        : shape === 'text'
          ? TEXT_R
          : CIRCLE_R;

// ── Shared node groups ───────────────────────────────────────────────────────
const toolsNodes = [
  {
    id: 'siem',
    shape: 'hex',
    x: 210,
    y: TIER_Y.tools,
    icon: 'siem',
    label: ['SIEM / SOAR'],
    phase: 0,
  },
  {
    id: 'privacy',
    shape: 'hex',
    x: 586,
    y: TIER_Y.tools,
    icon: 'privacy',
    label: ['PRIVACY TOOLS'],
    phase: 0,
  },
  {
    id: 'cloud',
    shape: 'hex',
    x: 961,
    y: TIER_Y.tools,
    icon: 'cloud',
    label: ['CLOUD TOOLS'],
    phase: 0,
  },
  { id: 'itsm', shape: 'hex', x: 1337, y: TIER_Y.tools, icon: 'itsm', label: ['ITSM'], phase: 0 },
];

const operatorNodes = [
  {
    id: 'testers',
    shape: 'circle',
    x: 292,
    y: TIER_Y.operators,
    icon: 'user',
    label: ['CONTROL', 'TESTERS'],
    phase: 1,
  },
  {
    id: 'engineers',
    shape: 'circle',
    x: 622,
    y: TIER_Y.operators,
    icon: 'user',
    label: ['SECURITY', 'ENGINEERS'],
    phase: 1,
  },
  {
    id: 'developers',
    shape: 'circle',
    x: 940,
    y: TIER_Y.operators,
    icon: 'user',
    label: ['DEVELOPERS'],
    phase: 1,
  },
  {
    id: 'owners',
    shape: 'circle',
    x: 1265,
    y: TIER_Y.operators,
    icon: 'user',
    label: ['CONTROL', 'OWNERS'],
    phase: 1,
  },
];

const governanceNodes = [
  {
    id: 'policy',
    shape: 'circle',
    x: 455,
    y: TIER_Y.governance,
    icon: 'user',
    label: ['POLICY & CONTROL', 'TEAMS'],
    phase: 2,
  },
  {
    id: 'auditors',
    shape: 'circle',
    x: 783,
    y: TIER_Y.governance,
    icon: 'user',
    label: ['INTERNAL', 'AUDITORS'],
    phase: 2,
  },
  {
    id: 'risk',
    shape: 'circle',
    x: 1103,
    y: TIER_Y.governance,
    icon: 'user',
    label: ['RISK', 'OWNERS'],
    phase: 2,
  },
];

const outputNodes = [
  {
    id: 'grc',
    shape: 'square',
    x: 440,
    y: TIER_Y.outputs,
    icon: 'grc',
    label: ['GRC TOOLING'],
    phase: 3,
  },
  {
    id: 'register',
    shape: 'square',
    x: 780,
    y: TIER_Y.outputs,
    icon: 'register',
    label: ['INTERNAL CONTROL', 'REGISTER'],
    phase: 3,
  },
  {
    id: 'extaudit',
    shape: 'square',
    x: 1120,
    y: TIER_Y.outputs,
    icon: 'extaudit',
    label: ['EXTERNAL', 'AUDITORS'],
    phase: 3,
  },
];

// Stance hub — replaces the operators tier in the right-hand variant.
const stanceNode = {
  id: 'stance',
  shape: 'logo',
  x: 780,
  y: STANCE_Y, // 1/3 mark + STANCE_DROP
  icon: 'stance',
  // No label — the caption block directly below names it.
  phase: 1,
};

// Right-variant caption — sits in the gap between the Stance hub and the
// outputs tier. Rendered as a shape:'text' node (no circle/icon) so the
// connector can funnel into its top and fan out of its bottom.
const testLayerNode = {
  id: 'testlayer',
  shape: 'text',
  x: 780,
  y: CAPTION_Y,
  phase: 1.6,
  eyebrow: 'INDEPENDENT TEST LAYER',
  heading: 'Automated Continuous Assurance',
  // `sub` renders as "With <link>", where the link is underlined and opens a
  // hover card. subCard is the card's copy — PLACEHOLDER, edit to taste.
  subPre: 'With ',
  subLink: 'Configurable Human Oversight',
  subCard: {
    title: 'Configurable Human Oversight',
    body: 'Decide where people stay in the loop. Set approval gates, review thresholds, and sign-off steps per control — so automation never runs ahead of your risk appetite.',
  },
};

// ── Edge sets ────────────────────────────────────────────────────────────────
const leftEdges = [
  // Tools → operators
  { from: 'siem', to: 'testers', phase: 1.0 },
  { from: 'siem', to: 'engineers', phase: 1.0 },
  { from: 'siem', to: 'auditors', phase: 1.0, bow: 150 },
  { from: 'privacy', to: 'engineers', phase: 1.0 },
  { from: 'privacy', to: 'developers', phase: 1.0 },
  { from: 'privacy', to: 'policy', phase: 1.0, bow: -120 },
  { from: 'cloud', to: 'developers', phase: 1.0 },
  { from: 'cloud', to: 'engineers', phase: 1.0 },
  { from: 'cloud', to: 'risk', phase: 1.0, bow: 140 },
  { from: 'itsm', to: 'owners', phase: 1.0 },
  { from: 'itsm', to: 'developers', phase: 1.0 },
  { from: 'itsm', to: 'testers', phase: 1.0, bow: -260 },
  // Operator → operator
  { from: 'testers', to: 'engineers', phase: 1.5 },
  { from: 'engineers', to: 'developers', phase: 1.5 },
  { from: 'developers', to: 'owners', phase: 1.5 },
  { from: 'testers', to: 'owners', phase: 1.5, bow: -190 },
  // Operators → governance
  { from: 'testers', to: 'policy', phase: 2.2 },
  { from: 'engineers', to: 'policy', phase: 2.2 },
  { from: 'engineers', to: 'auditors', phase: 2.2 },
  { from: 'developers', to: 'auditors', phase: 2.2 },
  { from: 'developers', to: 'risk', phase: 2.2 },
  { from: 'owners', to: 'risk', phase: 2.2, bow: 130 },
  // Governance → outputs — landTop: end on the box's top edge (not its center)
  // so the line + pulse stop at the box and don't ride over the white box.
  { from: 'policy', to: 'grc', phase: 3.0, landTop: true },
  { from: 'policy', to: 'register', phase: 3.0, landTop: true },
  { from: 'auditors', to: 'register', phase: 3.0, landTop: true },
  { from: 'auditors', to: 'extaudit', phase: 3.0, landTop: true },
  { from: 'risk', to: 'grc', phase: 3.0, bow: 120, landTop: true },
  { from: 'risk', to: 'extaudit', phase: 3.0, landTop: true },
];

// Right variant — everything funnels through the Stance hub straight to the
// outputs (no operators/governance tiers).
const rightEdges = [
  // Tools → Stance — elbow routing; outer tools bend higher (0.42), inner ones
  // lower (0.68) so the horizontal runs don't overlap.
  { from: 'siem', to: 'stance', phase: 1.0, turnFrac: 0.42 },
  { from: 'privacy', to: 'stance', phase: 1.0, turnFrac: 0.68 },
  { from: 'cloud', to: 'stance', phase: 1.0, turnFrac: 0.68 },
  { from: 'itsm', to: 'stance', phase: 1.0, turnFrac: 0.42 },
  // Caption (grouped under the hub, no connector) → outputs — elbow fan: the
  // sides bend out, the middle runs straight down.
  { from: 'testlayer', to: 'grc', phase: 2.0, turnFrac: 0.55 },
  { from: 'testlayer', to: 'register', phase: 2.0 },
  { from: 'testlayer', to: 'extaudit', phase: 2.0, turnFrac: 0.55 },
];

const PHASE_MS = 480;

// Path through a series of right-angle waypoints with rounded corners: each
// interior corner is cut back by `r` (clamped to half the shorter leg) and
// bridged with a quadratic through the true corner. Collinear/duplicate points
// collapse to a straight segment.
function roundedPath(pts, r) {
  const p = pts.filter((pt, i) => i === 0 || pt[0] !== pts[i - 1][0] || pt[1] !== pts[i - 1][1]);
  if (p.length < 3) return `M${p[0][0]},${p[0][1]} L${p[p.length - 1][0]},${p[p.length - 1][1]}`;
  let d = `M${p[0][0]},${p[0][1]}`;
  for (let i = 1; i < p.length - 1; i++) {
    const [x0, y0] = p[i - 1];
    const [x1, y1] = p[i];
    const [x2, y2] = p[i + 1];
    const l1 = Math.hypot(x0 - x1, y0 - y1) || 1;
    const l2 = Math.hypot(x2 - x1, y2 - y1) || 1;
    const rr = Math.min(r, l1 / 2, l2 / 2);
    const ax = x1 + ((x0 - x1) / l1) * rr;
    const ay = y1 + ((y0 - y1) / l1) * rr;
    const bx = x1 + ((x2 - x1) / l2) * rr;
    const by = y1 + ((y2 - y1) / l2) * rr;
    d += ` L${ax},${ay} Q${x1},${y1} ${bx},${by}`;
  }
  const last = p[p.length - 1];
  d += ` L${last[0]},${last[1]}`;
  return d;
}

function renderDiagram(container, { nodes, edges, idPrefix, pulseSpeed = 1 }) {
  const byId = new Map(nodes.map((n) => [n.id, n]));

  // Bowed cubic between two node centers.
  function edgePath(e, i) {
    const a = byId.get(e.from);
    const b = byId.get(e.to);
    // Anchor points. Default: node centers (opaque fills hide the ends). Funnel:
    // exit the source's bottom edge and arrive at the target's top edge, so all
    // edges sharing a node converge to a single point on it.
    let ax = a.x;
    let ay = a.y;
    let bx = b.x;
    let by = b.y;
    if (e.funnel || e.elbow) {
      ay = a.y + nodeRadius(a.shape);
      by = b.y - nodeRadius(b.shape);
    }
    // Land on the target's top edge instead of its center, so the connector
    // (and the pulse riding it) stops at the box top and never rides over the
    // white box. Used for edges into the last (outputs) tier of the left web.
    if (e.landTop) by = b.y - nodeRadius(b.shape);
    // Elbow: straight down, a rounded 90° bend, straight across, another bend,
    // straight down into the target (vertical–horizontal–vertical). `turnFrac`
    // (0..1) sets where along the vertical span the horizontal run sits.
    if (e.elbow) {
      if (Math.abs(bx - ax) < 1) return `M${ax},${ay} L${bx},${by}`; // aligned → straight
      const turnY = ay + (by - ay) * (e.turnFrac ?? 0.5);
      return roundedPath(
        [
          [ax, ay],
          [ax, turnY],
          [bx, turnY],
          [bx, by],
        ],
        e.radius ?? ELBOW_R,
      );
    }
    const dx = bx - ax;
    const dy = by - ay;
    const dist = Math.hypot(dx, dy) || 1;
    const nx = -dy / dist;
    const ny = dx / dist;
    // Two cubic control points shape the curve. Override them per-edge with
    // `via: [[x1,y1],[x2,y2]]` (absolute viewBox coords) to hand-manage a
    // curve; otherwise they're auto-placed at 1/3 & 2/3, offset by `bow`.
    let c1x, c1y, c2x, c2y;
    if (e.via) {
      [[c1x, c1y], [c2x, c2y]] = e.via;
    } else {
      const bow = e.bow ?? Math.min(dist * 0.22, 150) * (i % 2 ? 1 : -1);
      c1x = ax + dx / 3 + nx * bow;
      c1y = ay + dy / 3 + ny * bow;
      c2x = ax + (dx * 2) / 3 + nx * bow;
      c2y = ay + (dy * 2) / 3 + ny * bow;
    }
    return `M${ax},${ay} C${c1x},${c1y} ${c2x},${c2y} ${bx},${by}`;
  }

  const svg = select(container)
    .append('svg')
    .attr('viewBox', `0 0 1560 ${TIER_Y.outputs + 310}`)
    .attr('role', 'img')
    .attr('aria-label', 'Diagram connecting security tools to the teams that operate them');

  const defs = svg.append('defs');

  // Horizontal text gradient for the caption heading — matches the site's
  // `.text-gradient-label` (cyan → blue → navy → blue → cyan). SVG <text> can't
  // use CSS background-clip, so it's a real <linearGradient> referenced by fill.
  const headingGradId = `tl-heading-grad-${idPrefix}`;
  const headingGrad = defs
    .append('linearGradient')
    .attr('id', headingGradId)
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '100%')
    .attr('y2', '0%');
  [
    ['0%', '#00bdf3'],
    ['25%', '#0057dc'],
    ['50%', '#1e004a'],
    ['75%', '#0057dc'],
    ['100%', '#00bdf3'],
  ].forEach(([offset, color]) =>
    headingGrad.append('stop').attr('offset', offset).attr('stop-color', color),
  );

  // Grouping frame behind the bottom "outputs" tier.
  const groupFrame = svg
    .append('rect')
    .attr('class', 'group-frame')
    .attr('x', 252)
    .attr('y', TIER_Y.outputs - 182)
    .attr('width', 1056)
    .attr('height', 460)
    .attr('rx', 4);

  const edgeSel = svg
    .append('g')
    .selectAll('path')
    .data(edges, (d) => d.from + '-' + d.to)
    .join('path')
    .attr('class', 'edge')
    .attr('d', edgePath);

  const nodeSel = svg
    .append('g')
    .selectAll('g.node')
    .data(nodes, (d) => d.id)
    .join('g')
    .attr('class', (d) => `node node--${d.shape}`)
    .attr('transform', (d) => `translate(${d.x}, ${d.y})`);

  // Top layer for hover pulses — dots that ride along the edges on mouse-over.
  const pulseLayer = svg.append('g').attr('class', 'pulse-layer');

  nodeSel.each(function (d) {
    const g = select(this);
    const r = nodeRadius(d.shape);

    if (d.shape === 'hex') {
      const w = (HEX_R * Math.sqrt(3)) / 2;
      g.append('polygon')
        .attr('class', 'hex')
        .attr(
          'points',
          [
            [0, -HEX_R],
            [w, -HEX_R / 2],
            [w, HEX_R / 2],
            [0, HEX_R],
            [-w, HEX_R / 2],
            [-w, -HEX_R / 2],
          ]
            .map((p) => p.join(','))
            .join(' '),
        );
    } else if (d.shape === 'square') {
      g.append('rect')
        .attr('class', 'box')
        .attr('x', -SQUARE_R)
        .attr('y', -SQUARE_R)
        .attr('width', SQUARE_R * 2)
        .attr('height', SQUARE_R * 2)
        .attr('rx', 4);
    } else if (d.shape === 'logo') {
      // Opaque disc hides the edge ends; the Stance mark sits on top.
      g.append('circle').attr('class', 'logo-bg').attr('r', LOGO_R);
    } else if (d.shape === 'text') {
      // Caption block — no shape/icon; three stacked, centered text rows.
      g.append('text').attr('class', 'tl-eyebrow').attr('y', CAPTION_ROWS.eyebrow).text(d.eyebrow);
      g.append('text')
        .attr('class', 'tl-heading')
        .attr('y', CAPTION_ROWS.heading)
        // Inline style (not attr) so it beats the `.tl-heading { fill }` rule —
        // a presentation attribute would lose to the stylesheet.
        .style('fill', `url(#${headingGradId})`)
        .text(d.heading);
      const sub = g.append('text').attr('class', 'tl-sub').attr('y', CAPTION_ROWS.sub);
      if (d.subLink) {
        // Prefix stays plain; the linked phrase is underlined + hoverable.
        sub.append('tspan').text(d.subPre ?? '');
        sub.append('tspan').attr('class', 'tl-sub-link').text(d.subLink);
      } else {
        sub.text(d.sub);
      }
      return; // no icon / node-label for the caption
    } else {
      g.append('circle').attr('class', 'person').attr('r', CIRCLE_R);
    }

    const size = iconSize(d.shape);
    // Inline the icon's own <svg> (viewBox handles scaling) and size/center it.
    g.append('g')
      .attr('class', 'icon')
      .html(ICONS[d.icon])
      .select('svg')
      .attr('x', -size / 2)
      .attr('y', -size / 2)
      .attr('width', size)
      .attr('height', size);

    if (!d.label) return; // node with no text label (e.g. the Stance hub)
    const labelGap = LABEL_GAP[d.phase] ?? 80;
    const label = g
      .append('text')
      .attr('class', 'node-label')
      .attr('y', r + labelGap);
    d.label.forEach((t, i) => {
      label
        .append('tspan')
        .attr('x', 0)
        .attr('dy', i === 0 ? 0 : LABEL_LINE)
        .text(t);
    });
  });

  /* Hover card — the underlined phrase in the caption (e.g. "Configurable Human
     Oversight") opens an HTML popover styled like the demo picker. The link is
     SVG text, so the card is an absolutely-positioned div inside `container`
     (which is position:relative), placed above the phrase via its client rect. */
  const linkNode = nodes.find((n) => n.subLink && n.subCard);
  const linkEl = linkNode && container.querySelector('.tl-sub-link');
  if (linkEl) {
    const card = document.createElement('div');
    card.className = 'eco-hovercard';
    card.setAttribute('role', 'tooltip');
    card.innerHTML = `<p class="eco-hovercard__title">${linkNode.subCard.title}</p><p class="eco-hovercard__body">${linkNode.subCard.body}</p>`;
    container.appendChild(card);

    let hideTimer = null;
    const show = () => {
      window.clearTimeout(hideTimer);
      const cRect = container.getBoundingClientRect();
      const lRect = linkEl.getBoundingClientRect();
      // Center horizontally on the phrase; anchor the card's bottom to its top.
      card.style.left = `${lRect.left - cRect.left + lRect.width / 2}px`;
      card.style.top = `${lRect.top - cRect.top}px`;
      card.dataset.open = 'true';
    };
    // Small delay lets the pointer travel from the text into the card.
    const hide = () => {
      hideTimer = window.setTimeout(() => (card.dataset.open = 'false'), 120);
    };

    linkEl.addEventListener('mouseenter', show);
    linkEl.addEventListener('mouseleave', hide);
    card.addEventListener('mouseenter', show);
    card.addEventListener('mouseleave', hide);
  }

  /* Load animation — dashed edges revealed through a growing white mask so the
     dashes stay dashes; nodes pop in with a back-ease. Gated to when the
     diagram scrolls into view (not page load): the hidden pre-draw state is set
     now, and the reveal transitions run once on-screen. This way the draw is
     actually seen AND always settles with every tier — including the last
     (outputs) tier — fully visible. */
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;

  // Frame fades in with the last (outputs) tier — derive from the max phase so
  // it adapts whether the diagram has 3 or 4 tiers.
  const framePhase = Math.max(...nodes.map((n) => n.phase));

  // Pre-draw hidden state (set immediately so nothing flashes fully-drawn).
  groupFrame.attr('opacity', 0);

  // Build each edge's reveal mask up-front in its hidden state; stash a handle
  // + timing so the intro can animate the dash offset to 0.
  const edgeReveals = [];
  edgeSel.each(function (d, i) {
    const len = this.getTotalLength();
    const maskId = `reveal-${idPrefix}-${d.from}-${d.to}`;
    const maskPath = defs
      .append('mask')
      .attr('id', maskId)
      .attr('maskUnits', 'userSpaceOnUse')
      .append('path')
      .attr('d', select(this).attr('d'))
      .attr('fill', 'none')
      .attr('stroke', '#fff')
      .attr('stroke-width', 8)
      .attr('stroke-dasharray', len)
      .attr('stroke-dashoffset', len);
    select(this).attr('mask', `url(#${maskId})`);
    edgeReveals.push({ maskPath, delay: d.phase * PHASE_MS + i * 35 });
  });

  nodeSel.attr('opacity', 0).attr('transform', (d) => `translate(${d.x}, ${d.y}) scale(0.85)`);

  let introRun = false;
  function runIntro() {
    if (introRun) return;
    introRun = true;

    groupFrame
      .transition()
      .delay(framePhase * PHASE_MS)
      .duration(480)
      .ease(easeCubicOut)
      .attr('opacity', 1);

    edgeReveals.forEach(({ maskPath, delay }) => {
      maskPath
        .transition()
        .delay(delay)
        .duration(700)
        .ease(easeCubicOut)
        .attr('stroke-dashoffset', 0);
    });

    nodeSel
      .transition()
      .delay((d) => d.phase * PHASE_MS)
      .duration(480)
      .ease(easeBackOut.overshoot(1.2))
      .attr('opacity', 1)
      .attr('transform', (d) => `translate(${d.x}, ${d.y}) scale(1)`);

    // Kick off the flowing pulses once the web has finished drawing, and leave
    // them running (no hover gating) — the "signal flowing through the web" is
    // always on.
    window.setTimeout(startPulses, framePhase * PHASE_MS + 700);
  }

  // Start when the diagram enters the viewport (fires immediately if it's
  // already in view on load). Fallback: run now if IntersectionObserver is
  // unavailable, so the diagram can never get stuck hidden.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries, obs) => {
        if (entries.some((e) => e.isIntersecting)) {
          runIntro();
          obs.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -15% 0px' },
    );
    io.observe(container);
  } else {
    runIntro();
  }

  /* Flowing pulses — fire glowing dots that travel along random edges (random
     direction, speed, and cadence), for a "signal flowing through the web"
     feel. Runs in bursts: emit for a window, then go quiet for ~1s (a visible
     empty beat) before resuming. `pulseSpeed` (>1 = slower) scales both travel
     time and emission cadence — the without-Stance web runs slower so the
     Stance web reads as faster by contrast. Started from runIntro once the web
     has drawn. */
  const edgePaths = edgeSel.nodes();
  const PULSE_ACTIVE_MS = 3000; // how long each emission window lasts
  const PULSE_REST_MS = 1000; // quiet gap between windows (no dots on screen)
  let emitTimer = null;
  let cycleTimer = null;

  function spawnPulse() {
    const path = edgePaths[Math.floor(Math.random() * edgePaths.length)];
    if (!path) return;
    const len = path.getTotalLength();
    // Always flow downward — travel toward whichever endpoint sits lower, so a
    // pulse never runs back up the web (horizontal edges just go start→end).
    const reverse = path.getPointAtLength(0).y > path.getPointAtLength(len).y;
    const dur = (650 + Math.random() * 700) * pulseSpeed;
    pulseLayer
      .append('circle')
      .attr('class', 'pulse')
      .attr('r', 10)
      .attr('opacity', 0)
      .transition()
      .duration(dur)
      .ease((t) => t) // linear — even travel speed
      .attrTween('transform', () => (t) => {
        const p = path.getPointAtLength((reverse ? 1 - t : t) * len);
        return `translate(${p.x},${p.y})`;
      })
      .attrTween('opacity', () => (t) => String(Math.sin(Math.PI * t))) // fade in/out
      .on('end', function () {
        select(this).remove();
      });
  }

  function startPulses() {
    if (document.hidden || emitTimer || cycleTimer) return;
    // Emit bursts on a self-scheduling timer within each active window.
    const emit = () => {
      const burst = 1 + Math.floor(Math.random() * 2); // 1–2 pulses per tick
      for (let k = 0; k < burst; k++) spawnPulse();
      emitTimer = window.setTimeout(emit, (220 + Math.random() * 380) * pulseSpeed);
    };
    // One cycle: emit for PULSE_ACTIVE_MS, fall quiet for PULSE_REST_MS (dots in
    // flight finish and none are spawned), then repeat.
    const cycle = () => {
      emit();
      cycleTimer = window.setTimeout(() => {
        window.clearTimeout(emitTimer);
        emitTimer = null;
        cycleTimer = window.setTimeout(cycle, PULSE_REST_MS);
      }, PULSE_ACTIVE_MS);
    };
    cycle();
  }

  function stopPulses() {
    window.clearTimeout(emitTimer);
    window.clearTimeout(cycleTimer);
    emitTimer = null;
    cycleTimer = null;
    // Kill any in-flight dots so a frozen backlog can't burst on return.
    pulseLayer.selectAll('.pulse').interrupt().remove();
  }

  // Pause pulses while the tab is hidden. In the background, setTimeout keeps
  // firing but the rAF-driven pulse transitions freeze — dots pile up and then
  // all burst out at once when you come back. Stop + clear on hide; resume once
  // visible (only after the intro has kicked pulses off).
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopPulses();
    else if (introRun) startPulses();
  });
}

const left = document.getElementById('ecosystem-web');
if (left) {
  renderDiagram(left, {
    nodes: [...toolsNodes, ...operatorNodes, ...governanceNodes, ...outputNodes],
    edges: leftEdges,
    idPrefix: 'l',
    // Without Stance: pulses travel (and emit) slower, so the Stance web reads
    // as faster by contrast.
    pulseSpeed: 2.4,
  });
}

const right = document.getElementById('ecosystem-web-right');
if (right) {
  renderDiagram(right, {
    // No operators/governance tiers — outputs re-phased to 2 so the cascade
    // (tools → stance → outputs) stays tight.
    nodes: [
      ...toolsNodes,
      stanceNode,
      testLayerNode,
      ...outputNodes.map((n) => ({ ...n, phase: 2 })),
    ],
    // Elbow: every edge exits the bottom of its source and lands on the top of
    // its target, routed as orthogonal lines with rounded 90° bends.
    edges: rightEdges.map((e) => ({ ...e, elbow: true })),
    idPrefix: 'r',
    // With Stance: faster-than-full-speed pulses (below 1 = quicker).
    pulseSpeed: 0.7,
  });
}
